import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import SpeedIcon from '@mui/icons-material/Speed';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import FunctionsIcon from '@mui/icons-material/Functions';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TableChartIcon from '@mui/icons-material/TableChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const content = {
  title: 'Stop Fighting Your Budgeting Software.',
  subtitle: 'BridgeFirst Forecasting is a process-driven financial planning platform that keeps your budget presentation-ready from day one — without the complexity of enterprise tools or the limitations of Excel.',
  ctaPrimary: 'Request a Demo',
  ctaSecondary: 'Learn More',

  problemSection: {
    title: 'Sound familiar?',
    subtitle: 'Most finance teams are stuck choosing between two bad options.',
    problems: [
      {
        heading: 'Excel is holding you back',
        body: 'Version control nightmares, broken formulas, manual consolidations across dozens of tabs. Excel was never designed for multi-entity financial planning at scale — and it shows.',
      },
      {
        heading: 'Enterprise software adds complexity, not efficiency',
        body: 'Industry-standard tools promise transformation but deliver months of implementation, steep learning curves, and licensing costs that require their own budget line.',
      },
      {
        heading: 'Your budget is never "ready"',
        body: 'Presentations get delayed because the numbers aren\'t formatted. Analysis takes days because the data isn\'t structured. Leadership can\'t drill down because the model doesn\'t support it.',
      },
    ],
  },

  howItWorks: {
    title: 'How BridgeFirst Works',
    subtitle: 'A structured process that keeps your team aligned and your budget ready to present — at every stage.',
    steps: [
      {
        number: '01',
        title: 'Upload Your Template',
        body: 'Import your existing Excel template with your entity, department, and account hierarchy. BridgeFirst parses your structure automatically — no manual re-entry, no reformatting.',
      },
      {
        number: '02',
        title: 'Build Your Bridge',
        body: 'Add bridge columns to model changes over time using point-and-click operators: add, subtract, multiply, or percentage. Each change is tracked, labeled, and auditable.',
      },
      {
        number: '03',
        title: 'Present with Confidence',
        body: 'Your budget is presentation-ready throughout the entire process. Drill down to any entity, department, or account. Export to Excel or CSV whenever you need it.',
      },
    ],
  },

  speedSection: {
    title: 'Built for Speed. Designed for Scale.',
    subtitle: 'BridgeFirst is built on modern application infrastructure — not a spreadsheet engine. That means real performance where it counts.',
    stats: [
      { value: '200k+', label: 'rows processed in seconds' },
      { value: '< 1s', label: 'variance analysis refresh' },
      { value: '∞', label: 'entities & departments' },
      { value: '100%', label: 'presentation-ready, always' },
    ],
  },

  whatIs: {
    title: 'What is BridgeFirst?',
    subtitle: 'A financial planning platform built around a process — not just a set of features. Every decision in BridgeFirst is designed to keep your team moving forward, your data clean, and your output ready to share.',
    bulletPoints: [
      {
        title: 'Always Presentation-Ready',
        subtitle: 'The BridgeFirst process keeps your budget formatted and drillable at every stage — not just at month-end when you\'re scrambling to prepare slides.',
        icon: <WaterfallChartIcon />,
      },
      {
        title: 'Familiar, But Faster',
        subtitle: 'Point-and-click calculations for speed, with full spreadsheet-style formula support for power users. You work the way you want to work.',
        icon: <DisplaySettingsIcon />,
      },
      {
        title: 'Automated Variance Analysis',
        subtitle: 'Budget vs. actual analysis is built in — pivotable and drillable to the account level. No more manual reconciliation in a separate spreadsheet.',
        icon: <AutoGraphIcon />,
      },
      {
        title: 'Modern Infrastructure',
        subtitle: 'Cloud-hosted, secure, and fast. BridgeFirst handles datasets that would crash Excel — without the wait.',
        icon: <SpeedIcon />,
      },
    ],
  },

  features: {
    title: 'Everything Your Finance Team Needs',
    subtitle: 'Purpose-built features for the full financial planning lifecycle.',
    items: [
      {
        title: 'Multi-Entity Support',
        body: 'Manage unlimited entities, departments, and accounts within a single project. Consolidations happen automatically.',
        icon: <AccountTreeIcon />,
      },
      {
        title: 'Bridge Columns',
        body: 'Model changes with labeled, auditable bridge columns. Every adjustment is tracked with an operator, date range, and description.',
        icon: <CompareArrowsIcon />,
      },
      {
        title: 'Stat Drivers',
        body: 'Link statistical drivers to financial accounts for automated, formula-driven forecasting across your entire model.',
        icon: <TrendingUpIcon />,
      },
      {
        title: 'Calculated Accounts',
        body: 'Create accounts with custom formulas that reference other accounts — no spreadsheet required.',
        icon: <FunctionsIcon />,
      },
      {
        title: 'Pro Forma Statements',
        body: 'Generate pro forma financial statements directly from your project data, formatted and ready to share.',
        icon: <BarChartIcon />,
      },
      {
        title: 'Excel Import & Export',
        body: 'Start from your existing Excel template. Export to CSV or Excel at any point — your data, your format.',
        icon: <CloudUploadIcon />,
      },
      {
        title: 'Variance Analysis',
        body: 'Budget vs. actual reporting that\'s pivotable and drillable to the lowest account level, updated in real time.',
        icon: <AutoGraphIcon />,
      },
      {
        title: 'Spreadsheet-Style Grid',
        body: 'An AG Grid-powered interface that feels familiar to Excel users — with the power of a real application behind it.',
        icon: <TableChartIcon />,
      },
    ],
  },

  comparison: {
    title: 'Why BridgeFirst?',
    subtitle: 'See how we stack up against the alternatives.',
    headers: ['', 'BridgeFirst', 'Excel', 'Enterprise Tools'],
    rows: [
      { feature: 'Presentation-ready output at all times', bf: true, excel: false, enterprise: false },
      { feature: 'Process-oriented workflow', bf: true, excel: false, enterprise: false },
      { feature: 'Multi-entity consolidation', bf: true, excel: false, enterprise: true },
      { feature: 'Auditable bridge columns', bf: true, excel: false, enterprise: false },
      { feature: 'Fast implementation (days, not months)', bf: true, excel: true, enterprise: false },
      { feature: 'Familiar spreadsheet interface', bf: true, excel: true, enterprise: false },
      { feature: 'Affordable for mid-market', bf: true, excel: true, enterprise: false },
      { feature: 'Built-in variance analysis', bf: true, excel: false, enterprise: true },
      { feature: 'No IT implementation required', bf: true, excel: true, enterprise: false },
    ],
  },

  testimonials: [
    {
      quote: 'BridgeFirst cut our monthly close process in half. We used to spend two weeks consolidating Excel files across 12 entities. Now it takes two days.',
      name: 'CFO',
      company: 'Regional Healthcare Group',
    },
    {
      quote: 'We several Enterprise solutions before finding BridgeFirst. The others would have taken 6 months to implement. BridgeFirst was live in two weeks.',
      name: 'VP of Finance',
      company: 'Multi-Location Retail Operator',
    },
    {
      quote: 'The bridge column process changed how our team thinks about budgeting. Every change is documented, every assumption is visible. Leadership finally trusts the numbers.',
      name: 'Director of FP&A',
      company: 'Professional Services Firm',
    },
  ],

  whoIs: {
    title: 'Who is BridgeFirst for?',
    subtitle: 'Built for medium to large businesses with complex entity structures who are ready to move beyond the limitations of their current tools.',
    bulletPoints: [
      {
        title: 'Frustrated with the Status Quo',
        subtitle: 'Your current tools — whether Excel or legacy software — are slowing your team down and producing output you\'re not proud to present.',
        icon: <SentimentVeryDissatisfiedIcon />,
      },
      {
        title: 'Stuck in Excel',
        subtitle: 'You\'re managing multi-entity budgets in spreadsheets and spending more time maintaining the model than actually analyzing the business.',
        icon: <FunctionsIcon />,
      },
      {
        title: 'Lean Finance Teams',
        subtitle: 'You want to do more with fewer FTEs — not by cutting corners, but by eliminating the manual work that shouldn\'t exist in the first place.',
        icon: <PersonRemoveIcon />,
      },
      {
        title: 'Leadership Accountability',
        subtitle: 'You want financial plans that leadership owns — with clear assumptions, visible changes, and output they can stand behind.',
        icon: <HandshakeIcon />,
      },
    ],
  },
};

export default content;
